import {
  Button,
  Col,
  Form,
  InputNumber,
  Layout,
  notification,
  Row,
  Select,
  Space,
  Typography,
  Input,
} from "antd";

import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { ISettingsService } from "@shared/interfaces/settings-service.interface";
import { SettingsDto } from "@shared/types/settings/settings.dto";
import { useEffect } from "react";
import { useMutation, useQuery } from "react-query";
import { SelectOptions } from "./Settings.const";
import styles from "./Settings.module.scss";
import { useWatch } from "antd/es/form/Form";

const Settings = () => {
  const [form] = Form.useForm();

  const freeDraftTokensEnabledString = useWatch("freeDraftTokensEnabled", form);
  const freeDraftTokensEnabled = Boolean(Number(freeDraftTokensEnabledString));

  const settingsService = appInject<ISettingsService>(
    DI_TOKENS.settingsService,
  );

  const getSettingsQuery = useQuery<SettingsDto>(
    ["get-settings"],
    () => settingsService.get(),
    {
      refetchOnMount: true,
    },
  );

  const updateSettingsMutation = useMutation(
    (data: SettingsDto): Promise<SettingsDto> => settingsService.update(data),
    {
      onSuccess: () => {
        notification.success({
          message: "Settings updated successfully",
        });
      },
    },
  );

  useEffect(() => {
    if (getSettingsQuery.data) {
      form.setFieldsValue({
        ...getSettingsQuery.data,
        forceUpdateApp: getSettingsQuery.data.forceUpdateApp ? "1" : "0",
        freeDraftTokensEnabled: getSettingsQuery.data.freeDraftTokensEnabled
          ? "1"
          : "0",
        inAppPurchaseEnabled: getSettingsQuery.data.inAppPurchaseEnabled
          ? "1"
          : "0",
        maintenanceModeEnabled: getSettingsQuery.data.maintenanceModeEnabled
          ? "1"
          : "0",
        extras: JSON.stringify(getSettingsQuery.data.extras || {}, null, 2),
      });
    }
  }, [form, getSettingsQuery.data]);

  const handleFinish = (data: SettingsDto) => {
    updateSettingsMutation.mutate({
      //   draftsPerDay: +data.draftsPerDay,
      //   draftsOnSignup: +data.draftsOnSignup,
      usableDraftTokensPerDay: +data.usableDraftTokensPerDay,
      draftsPerReferral: +data.draftsPerReferral,
      draftTokensPerDay: +data.draftTokensPerDay,
      viewSolutionLimitPerDay: +data.viewSolutionLimitPerDay,
      initialFreeSolutionLimit: +data.initialFreeSolutionLimit,
      freeDraftTokensEnabled:
        (data.freeDraftTokensEnabled as unknown as string) === "1",
      forceUpdateApp: (data.forceUpdateApp as unknown as string) === "1",
      inAppPurchaseEnabled:
        (data.inAppPurchaseEnabled as unknown as string) === "1",
      maintenanceModeEnabled:
        (data.maintenanceModeEnabled as unknown as string) === "1",
      sessionReplayCount: +data.sessionReplayCount,
      minScoreToUnlockRankedMode: +data.minScoreToUnlockRankedMode,
      draftsToUnlockRankedMode: +data.draftsToUnlockRankedMode,
      extras: JSON.parse((data.extras as unknown as string) || "{}"),
    });
  };

  return (
    <Layout className={styles.layout}>
      <Row style={{ height: 32, justifyContent: "space-between" }}>
        <Col span={12}>
          <Typography.Title style={{ margin: 0 }} level={5}>
            Settings
          </Typography.Title>
        </Col>
      </Row>
      <Layout.Content
        className={styles.content}
        style={{ backgroundColor: "white" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Form
            form={form}
            requiredMark={false}
            onFinish={handleFinish}
            layout="vertical"
          >
            <Row gutter={[16, 16]}>
              {/* <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftsPerDay"
                  label="Drafts Per Day"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col> */}

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftsPerReferral"
                  label="Drafts Per Referral"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="forceUpdateApp"
                  label="Force Update App"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="usableDraftTokensPerDay"
                  label="Draft Tokens Limit Per Day"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="inAppPurchaseEnabled"
                  label="Enable In App Purchase"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="freeDraftTokensEnabled"
                  label="Enable Daily Draft Tokens"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftTokensPerDay"
                  label="Daily Draft Tokens"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    disabled={!freeDraftTokensEnabled}
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="viewSolutionLimitPerDay"
                  label="View Solution Limit Per Day"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="initialFreeSolutionLimit"
                  label="Initial Free Solution Views for New Users"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="start" gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="maintenanceModeEnabled"
                  label="Enable Maintenance Mode"
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select options={SelectOptions} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="sessionReplayCount"
                  label="Session Replay Count"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="minScoreToUnlockRankedMode"
                  label="Min Score To Unlock Ranked Mode"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12} md={12} lg={12}>
                <Form.Item
                  name="draftsToUnlockRankedMode"
                  label="Drafts To Unlock Ranked Mode"
                  rules={[
                    {
                      type: "number",
                      message: "This must be a number",
                      required: true,
                    },
                  ]}
                >
                  <InputNumber
                    style={{
                      width: "100%",
                    }}
                  />
                </Form.Item>
              </Col>
            </Row> */}



            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="extras"
                  label="Extra Settings (JSON)"
                  tooltip="Arbitrary key-value pairs stored in Redis. Must be valid JSON."
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        try {
                          JSON.parse(value);
                          return Promise.resolve();
                        } catch (e) {
                          return Promise.reject(new Error("Invalid JSON format"));
                        }
                      },
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={10}
                    style={{ fontFamily: "monospace" }}
                    placeholder='{ "key": "value" }'
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end" style={{ marginTop: 16 }}>
              <Col>
                <Button
                  loading={updateSettingsMutation.isLoading}
                  disabled={updateSettingsMutation.isLoading}
                  size="middle"
                  type="primary"
                  htmlType="submit"
                >
                  Update
                </Button>
              </Col>
            </Row>
          </Form>
        </Space>
      </Layout.Content>
    </Layout>
  );
};

export default Settings;
